import React, { useState } from 'react';
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/it";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./custom_calendar.scss"; // Assicurati che il percorso sia corretto

moment.locale("it");
const localizer = momentLocalizer(moment);

const messages = {
  allDay: "Tutto il giorno",
  previous: "Precedente",
  next: "Successivo",
  today: "Oggi",
  month: "Mese",
  week: "Settimana",
  day: "Giorno",
  agenda: "Agenda",
  date: "Data",
  time: "Ora",
  event: "Evento",
  noEventsInRange: "Non ci sono eventi in questo intervallo.",
  showMore: (total) => `+ Vedi altri (${total})`,
};

const CustomCalendar = ({
  events,
  onSelectSlot,
  onSelectEvent,
  minDate,
  maxDate,
  isLoading,
  eventCursor = 'pointer',
  slotCursor = 'pointer',
  customEventPropGetter,
  customToolbar, // Mantieni la possibilità di passare una toolbar custom
  availableViews = ['month', 'agenda'], // Default a month e agenda
  ...props
}) => {

  //const [currentView, setCurrentView] = useState(availableViews[0] || 'month'); // Usa 'month' o la prima vista disponibile
  const [currentView, setCurrentView] = useState('agenda');
  const [currentDate, setCurrentDate] = useState(new Date());


  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleNavigate = (newDate, view, action) => {
    setCurrentDate(newDate);
  };

  // Aggiunge la prop isAgenda agli eventi per differenziare lo stile se necessario
  const enhancedEvents = events.map(event => ({
    ...event,
    isAgenda: currentView === 'agenda'
  }));

  // Gestore per la selezione di uno slot (giorno/ora)
  const handleSelectSlot = (slotInfo) => {
    if (!isLoading) {
      const slotDate = new Date(slotInfo.start);
       // Permetti la selezione solo nella vista mese e se la data è valida
       if (currentView === 'month' && (!minDate || slotDate >= minDate) && (!maxDate || slotDate <= maxDate)) {
        if (onSelectSlot) {
            onSelectSlot(slotInfo);
        }
      }
    }
  };

  // Gestore per la selezione di un evento esistente
  const handleSelectEvent = (event) => {
    if (!isLoading && onSelectEvent) {
      onSelectEvent(event);
    }
  };

  // Funzione per applicare stili agli eventi
  const eventStyleGetter = (event, start, end, isSelected) => {
    // Non applicare stili speciali se siamo in vista agenda (potrebbe sovrascrivere stili del tema)
    if (event?.isAgenda) {
      return { style: {} };
    }

    // Stili di default
    let defaultStyle = {
      backgroundColor: isLoading ? '#E0E0E0' : event.color || '#3174ad', // Grigio chiaro se loading, colore evento o default blu
      borderRadius: '5px',
      opacity: isLoading ? 0.6 : 1,
      color: isLoading ? '#666' : 'white', // Grigio scuro se loading, bianco altrimenti
      border: '0px',
      cursor: isLoading ? 'not-allowed' : eventCursor, // Cursore non permesso se loading
      padding: '2px 5px',
      fontSize: '0.85em',
      lineHeight: '1.4',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    // Se fornita, applica la funzione custom per stili aggiuntivi
    if (customEventPropGetter) {
      const customProps = customEventPropGetter(event, start, end, isSelected);
      // Unisci gli stili, dando priorità a quelli custom
      return {
        ...customProps, // Passa eventuali props custom (es. className)
        style: {
          ...defaultStyle,
          ...(customProps.style || {}) // Unisci gli oggetti stile
        }
      };
    }

    // Altrimenti, ritorna solo gli stili di default
    return { style: defaultStyle };
  };

  // Componente custom per visualizzare l'evento nel calendario
  const CustomEvent = ({ event }) => {
    // Ritorna semplicemente il titolo con il cursore appropriato
    return (
      <span style={{ cursor: isLoading ? 'not-allowed' : eventCursor }}>
        {event.title}
      </span>
    );
  };

  // Componente custom per l'orario nella vista Agenda
  const CustomAgendaTime = ({ event }) => {
    // Mostra l'intervallo orario e lo rende cliccabile
    return (
      <div
        style={{ cursor: isLoading ? 'not-allowed' : eventCursor, color: 'inherit' }}
        onClick={() => !isLoading && handleSelectEvent(event)} // Chiama handleSelectEvent al click (se non loading)
      >
        {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
      </div>
    );
  };

  // Toolbar personalizzata
  const DefaultToolbar = ({ label, onView, view, views, onNavigate, date }) => {
    const getTitle = () => {
        // Formatta il titolo in base alla vista corrente (invariato)
        if (view === 'month') {
            return `${moment(date).format('MMMM YYYY')} - Calendario Esami`;
        }
        if (view === 'agenda') {
             return `Agenda Esami - ${moment(date).format('MMMM YYYY')}`;
        }
        return 'Calendario Esami';
    };

    return (
      <div className="rbc-toolbar d-flex justify-content-between align-items-center mb-3">
        {/* Pulsanti di navigazione (Oggi, Precedente, Successivo) */}
        <div className="d-flex align-items-center" style={{ minWidth: '200px' }}>
             {/* ***** MODIFICA QUI: Rimuovi la condizione view !== 'agenda' ***** */}
             {/* Ora i pulsanti sono sempre visibili */}
             <>
                <button onClick={() => onNavigate('TODAY')} className="btn btn-outline-primary btn-sm me-2">Oggi</button>
                <button onClick={() => onNavigate('PREV')} className="btn btn-outline-primary btn-sm me-2"><i className="bi bi-chevron-left"></i></button>
                <button onClick={() => onNavigate('NEXT')} className="btn btn-outline-primary btn-sm"><i className="bi bi-chevron-right"></i></button>
             </>
             {/* ***** FINE MODIFICA ***** */}
        </div>

        {/* Titolo centrale (invariato)*/}
        <div className="text-center flex-grow-1 mx-3">
          <h4 className="text-primary mb-0">{getTitle()}</h4>
        </div>

        {/* Pulsanti per cambiare vista (Mese, Agenda) (invariato)*/}
        <div className="d-flex align-items-center" style={{ minWidth: '200px', justifyContent: 'flex-end' }}>
          {views.includes('month') && (
            <button
              onClick={() => onView('month')}
              className={`btn btn-sm ${view === 'month' ? 'btn-primary' : 'btn-outline-primary'} ${views.includes('agenda') ? 'me-2' : ''}`}
            >
              <i className="bi bi-calendar-month me-1"></i> Calendario
            </button>
          )}
           {views.includes('agenda') && (
            <button
              onClick={() => onView('agenda')}
              className={`btn btn-sm ${view === 'agenda' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              <i className="bi bi-list-ul me-1"></i> Agenda
            </button>
          )}
        </div>
      </div>
    );
  };

  // Funzione per applicare stili ai giorni (es. disabilitare giorni fuori range)
  const dayPropGetter = (date) => {
    const dateMoment = moment(date);
    const minMoment = minDate ? moment(minDate).startOf('day') : null;
    const maxMoment = maxDate ? moment(maxDate).startOf('day') : null;

    // Se sta caricando o la data è fuori dal range consentito
    if (isLoading || (minMoment && dateMoment.isBefore(minMoment)) || (maxMoment && dateMoment.isAfter(maxMoment))) {
      return {
        className: "disabled-day", // Applica una classe per lo stile CSS
        style: {
          cursor: "not-allowed",
          backgroundColor: "#f8f9fa", // Grigio molto chiaro
          // color: "#adb5bd", // Colore testo disabilitato (opzionale)
        },
      };
    } else {
      // Altrimenti, giorno selezionabile
      return {
        className: "selectable-day",
        style: {
          cursor: currentView === 'month' ? slotCursor : 'default', // Cursore solo in vista mese
        },
      };
    }
  };

  return (
    <Calendar
      localizer={localizer}
      events={enhancedEvents}
      startAccessor="start"
      endAccessor="end"
      view={currentView} // Controlla la vista corrente
      onView={handleViewChange} // Permette di cambiare vista
      date={currentDate} // Controlla la data corrente
      onNavigate={handleNavigate} // Permette di navigare tra date
      style={{ height: props.height || "calc(100vh - 200px)" }} // Altezza flessibile
      selectable={currentView === 'month' && !isLoading} // Selezionabile solo in vista mese
      onSelectSlot={handleSelectSlot} // Gestore selezione slot
      onSelectEvent={handleSelectEvent} // Gestore selezione evento
      min={minDate} // Data minima visualizzabile/selezionabile
      max={maxDate} // Data massima visualizzabile/selezionabile
      messages={messages} // Messaggi tradotti
      views={availableViews} // Viste disponibili (passate come prop o default)
      defaultView={availableViews[0] || 'month'} // Vista iniziale
      formats={{ // Formati data/ora personalizzati
        monthHeaderFormat: "MMMM YYYY",
        weekdayFormat: "dddd", // Nome giorno completo
        dayHeaderFormat: "dddd D MMMM",
        dayRangeHeaderFormat: ({ start, end }, culture, local) =>
            local.format(start, 'D MMMM', culture) + ' – ' + local.format(end, 'D MMMM YYYY', culture),
        agendaDateFormat: (date, culture, local) => local.format(date, 'dddd DD MMMM YYYY', culture), // Formato data agenda
        agendaHeaderFormat: ({ start, end }, culture, local) => // Formato titolo agenda
            local.format(start, 'DD MMMM YYYY', culture) + ' – ' + local.format(end, 'DD MMMM YYYY', culture),
        // Aggiungi altri formati se necessario
      }}
      dayPropGetter={dayPropGetter} // Applica stili ai giorni
      eventPropGetter={eventStyleGetter} // Applica stili agli eventi
      components={{ // Sostituisce componenti standard con quelli custom
        event: CustomEvent, // Per la vista mese/settimana/giorno
        agenda: {
          time: CustomAgendaTime, // Per l'orario in agenda
          event: CustomEvent    // Per l'evento in agenda
        },
        toolbar: (toolbarProps) => { // Usa la toolbar custom o quella di default definita sopra
          const ToolbarComponent = customToolbar || DefaultToolbar;
          return <ToolbarComponent {...toolbarProps} />;
        },
      }}
       // Quando si clicca "+ X altri" nella vista mese, passa all'agenda per quella data
       onShowMore={(events, date) => {
        setCurrentView('agenda');
        setCurrentDate(date);
      }}
      // Passa eventuali altre props non gestite esplicitamente
      {...props}
    />
  );
};

export default CustomCalendar;